<template>
  <div class="mural-kpis" :class="{ 'mural-kpis--scroll': scroll }">
    <!-- Receita: só existe quando o backend a envia. A ausência da chave é o
         sinal de que o usuário não pode ver valores — o professor, por exemplo,
         nunca recebe este bloco. -->
    <div v-if="summary.revenue" class="mural-kpi">
      <span class="mural-kpi__icon mural-kpi__icon--success">
        <feather-icon icon="DollarSignIcon" size="20" />
      </span>
      <div class="mural-kpi__body">
        <div class="mural-kpi__value hb-money">{{ money(summary.revenue.net) }}</div>
        <div class="mural-kpi__label">{{ $t('mural.kpi.revenue') }}</div>
        <div class="mural-kpi__foot" :class="deltaClass(summary.revenue.delta_pct)">
          {{ formatPercent(summary.revenue.delta_pct) }} {{ comparisonLabel }}
        </div>
      </div>
      <span
        class="mural-kpi__eye"
        :title="$t('mural.kpi.toggle_values')"
        @click="$emit('toggle-values')">
        <feather-icon :icon="moneyHidden ? 'EyeIcon' : 'EyeOffIcon'" size="17" />
      </span>
    </div>

    <div class="mural-kpi">
      <span class="mural-kpi__icon mural-kpi__icon--primary">
        <feather-icon icon="CalendarIcon" size="20" />
      </span>
      <div class="mural-kpi__body">
        <div class="mural-kpi__value">{{ summary.lessons.scheduled }}</div>
        <div class="mural-kpi__label">{{ $t('mural.kpi.lessons') }}</div>
        <div class="mural-kpi__foot text-muted">
          {{ $t('mural.kpi.lessons_sub', { done: summary.lessons.done }) }}
        </div>
      </div>
    </div>

    <div class="mural-kpi">
      <span class="mural-kpi__icon mural-kpi__icon--info">
        <feather-icon icon="PieChartIcon" size="20" />
      </span>
      <div class="mural-kpi__body">
        <div class="mural-kpi__value">{{ percent(summary.occupancy.rate) }}</div>
        <div class="mural-kpi__label">{{ $t('mural.kpi.occupancy') }}</div>
        <div class="mural-kpi__foot" :class="deltaClass(summary.occupancy.delta_pp)">
          {{ formatPoints(summary.occupancy.delta_pp) }} {{ comparisonLabel }}
        </div>
      </div>
    </div>

    <div class="mural-kpi">
      <span class="mural-kpi__icon mural-kpi__icon--warning">
        <feather-icon icon="UserPlusIcon" size="20" />
      </span>
      <div class="mural-kpi__body">
        <div class="mural-kpi__value">{{ summary.new_students.total }}</div>
        <div class="mural-kpi__label">{{ $t('mural.kpi.new_students') }}</div>
        <div class="mural-kpi__foot text-muted">
          {{ $t('mural.kpi.new_students_sub', {
            plan: summary.new_students.by_plan,
            single: summary.new_students.single,
          }) }}
        </div>
      </div>
    </div>

    <div class="mural-kpi">
      <span class="mural-kpi__icon mural-kpi__icon--danger">
        <feather-icon icon="XCircleIcon" size="20" />
      </span>
      <div class="mural-kpi__body">
        <div class="mural-kpi__value">{{ summary.disruptions.total }}</div>
        <div class="mural-kpi__label">{{ $t('mural.kpi.disruptions') }}</div>
        <div class="mural-kpi__foot text-muted">
          {{ $t('mural.kpi.disruptions_sub', {
            cancelled: summary.disruptions.cancelled,
            rescheduled: summary.disruptions.rescheduled,
          }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { currencyFormatter } from '@/util/Util'

/**
 * Os cinco números do período.
 *
 * Nenhuma decisão de permissão acontece aqui: o card de receita aparece se — e
 * somente se — o payload trouxer `revenue`. Quem decide é o backend.
 */
export default {
  name: 'MuralKpiRow',
  props: {
    summary: { type: Object, required: true },
    moneyHidden: { type: Boolean, default: false },
    period: { type: String, default: 'week' },
    scroll: { type: Boolean, default: false },
  },
  computed: {
    comparisonLabel() {
      const map = {
        week: this.$t('mural.period.previous_week'),
        month: this.$t('mural.period.previous_month'),
      }
      return map[this.period] || this.$t('mural.period.previous_period')
    },
  },
  methods: {
    money(value) {
      return currencyFormatter(Number(value || 0))
    },
    percent(value) {
      return value === null || value === undefined ? this.$t('mural.kpi.no_data') : `${value}%`
    },
    /**
     * Sem janela anterior para comparar (clube novo, receita zero antes), o
     * backend manda null — mostrar "0%" ali seria afirmar estabilidade que não
     * foi medida.
     */
    formatPercent(value) {
      if (value === null || value === undefined) return this.$t('mural.kpi.no_data')
      return `${value > 0 ? '+' : ''}${String(value).replace('.', ',')}%`
    },
    formatPoints(value) {
      if (value === null || value === undefined) return this.$t('mural.kpi.no_data')
      return `${value > 0 ? '+' : ''}${value} p.p.`
    },
    deltaClass(value) {
      if (value === null || value === undefined || value === 0) return 'text-muted'
      return value > 0 ? 'text-success' : 'text-danger'
    },
  },
}
</script>

<style lang="scss" scoped>
.mural-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* Em tela estreita a grade vira faixa rolável: cinco cards empilhados
   empurrariam a agenda para fora da primeira dobra. */
.mural-kpis--scroll {
  display: flex;
  overflow-x: auto;
  padding-bottom: 0.25rem;

  .mural-kpi {
    flex: none;
    min-width: 218px;
  }
}

.mural-kpi {
  background: var(--surface-card);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  min-width: 0;

  &__icon {
    width: 42px;
    height: 42px;
    flex: none;
    border-radius: var(--radius-pill);
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &--success { background: var(--tint-success); color: var(--success); }
    &--primary { background: var(--tint-primary); color: var(--primary); }
    &--info { background: var(--tint-info); color: var(--info); }
    &--warning { background: var(--tint-warning); color: var(--warning); }
    &--danger { background: var(--tint-danger); color: var(--danger); }
  }

  &__body {
    min-width: 0;
    flex: 1;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: var(--fw-medium);
    color: var(--text-heading);
    line-height: 1.2;
    white-space: nowrap;
  }

  &__label {
    font-size: 0.8rem;
    color: var(--text-body);
  }

  &__foot {
    margin-top: 0.15rem;
    font-size: 0.78rem;
  }

  &__eye {
    cursor: pointer;
    padding: 8px;
    margin: -8px;
    display: inline-flex;
    align-self: flex-start;
    flex: none;
    color: var(--text-muted);
  }
}
</style>
